// ─────────────────────────────────────────────────────────────────────────────
// segments — read-side helpers for the v2 round segment spec (#1396 / M8.8 2a-4).
//
// The round payload (api.round / RoundSummary) exposes `segments`: an ordered
// list of v2 blocks, each
//   { distance, unit, ends, arrows_per_end?, face_type?, face_size_cm?, label? }
// where `distance` is NATIVE in `unit` ("m" | "yd"). This mirrors the backend's
// backend/app/segments.py (segment_for_end / display formatting) so the read
// paths can show the archer's native unit (yd/m) and the per-stage face WITHOUT
// recomputing any score. These are pure presentation helpers — no scoring logic
// lives here (that stays in lib/scoring.js); they only map an end to its segment
// and format distance/face labels for display.
//
// Why native unit matters: End.distance_m is ALWAYS metres, so a yard round's
// ends carry the converted value (a York 100yd end is stored as 91m). Displaying
// End.distance_m raw therefore shows the wrong number to an imperial archer; the
// segment carries the native distance + unit for display. See the issue:
// "York end shows '100 yd', not '91 m'".
// ─────────────────────────────────────────────────────────────────────────────

import { faceIsCourse } from "./ruleSets";

/**
 * Is this round walked as a COURSE (Field / Hunter / 3D — target to target)
 * rather than shot as distance stages? Course rounds get NO break UI: no stage
 * cue, no per-break subtotal bands.
 *
 * THE one answer for a ROUND payload (which carries `target_face` but no
 * `structure_type`); the catalog-row twin is `courseFormat.isCourseRoundType`.
 * Decided by the FACE first — mirroring the backend's
 * `is_course = target_face in course_face_codes()` — because a custom MARKED
 * course Format (#2399/#2400) materialises one segment PER TARGET, each with
 * its own yard distance, so "every segment distance is null" is false for it
 * even though it is every bit a course (sandbox QA 2026-09-18, round 488:
 * "Take your break" after every target, one scorecard band per end). The
 * all-distances-null heuristic stays only as the fallback for callers that
 * know no face (legacy / partial payloads).
 *
 * @param {string|null|undefined} targetFace  round.target_face (face code)
 * @param {Array|null|undefined} segments     round.segments (v2)
 * @returns {boolean}
 */
export function isCourseRound(targetFace, segments) {
  if (targetFace && faceIsCourse(targetFace)) return true;
  if (!Array.isArray(segments) || segments.length === 0) return false;
  // A course segment carries NO distance in either the v2 (`distance`) or the
  // legacy v1 (`distance_m`) shape; checking both keeps v1 metric segments
  // (which only have `distance_m`) from being mis-read as course rounds.
  return segments.every((s) => s?.distance == null && s?.distance_m == null);
}

/**
 * The segment covering a 1-indexed `endNumber`, walking the cumulative `ends`
 * ranges in shooting order (mirrors backend segment_for_end). Returns null when
 * there are no segments or the end falls past the defined schedule (e.g. an
 * extra end beyond the planned round). The returned object is the raw v2
 * segment from the payload.
 *
 * @param {Array|null|undefined} segments  round.segments (v2)
 * @param {number} endNumber  1-indexed end number
 * @returns {object|null}
 */
export function segmentForEnd(segments, endNumber) {
  if (!segments || segments.length === 0) return null;
  let cursor = 0;
  for (const seg of segments) {
    const segEnds = Number(seg?.ends) || 0;
    if (endNumber <= cursor + segEnds) return seg;
    cursor += segEnds;
  }
  return null;
}

/**
 * Display string for a segment's NATIVE distance + unit, e.g. "100 yd", "70 m".
 * Returns null when the segment carries no distance (course / 3D rounds) — the
 * caller then renders no distance chip. `unit` defaults to "m".
 *
 * @param {object|null} segment  a v2 segment
 * @returns {string|null}
 */
export function formatSegmentDistance(segment) {
  if (!segment || segment.distance == null) return null;
  const unit = segment.unit || "m";
  return `${segment.distance} ${unit}`;
}

/**
 * Display label for a segment's face SIZE, e.g. "122cm", "80cm", or null when
 * the segment has no physical size (course / 3D rounds, or legacy rounds with no
 * per-segment size). The WA1440 mid-round face change is a SIZE change
 * (122cm → 80cm), so the size is the meaningful per-stage face label. Trailing
 * ".0" is trimmed so 60.0 → "60cm".
 *
 * @param {object|null} segment  a v2 segment
 * @returns {string|null}
 */
export function formatSegmentFace(segment) {
  if (!segment || segment.face_size_cm == null) return null;
  const cm = Number(segment.face_size_cm);
  if (!Number.isFinite(cm)) return null;
  const text = Number.isInteger(cm) ? String(cm) : String(cm).replace(/\.0$/, "");
  return `${text}cm`;
}

/**
 * True when a round's segments change FACE mid-round — i.e. two consecutive
 * segments differ in `face_type` or `face_size_cm` (e.g. WA1440 122cm → 80cm).
 * Used to decide whether to surface a per-stage face label at all; a normal
 * single-face round shows nothing extra (no regression). A round with one
 * segment, or no segments, returns false.
 *
 * @param {Array|null|undefined} segments  round.segments (v2)
 * @returns {boolean}
 */
export function hasFaceChange(segments) {
  if (!segments || segments.length < 2) return false;
  for (let i = 1; i < segments.length; i++) {
    const a = segments[i - 1];
    const b = segments[i];
    if ((a?.face_type ?? null) !== (b?.face_type ?? null)) return true;
    if ((a?.face_size_cm ?? null) !== (b?.face_size_cm ?? null)) return true;
  }
  return false;
}

/**
 * Break-boundary info for a 1-indexed `endNumber` (#1399).
 *
 * A round is an ordered list of BREAKS (sub-rounds) — the segment list IS the
 * break list. This returns non-null ONLY when `endNumber` is the FIRST end of a
 * break OTHER than the first (i.e. the archer has just crossed into a new
 * sub-round). It fires on EVERY break boundary, including a break at the SAME
 * distance as the previous one (WA Indoor 600 = 2×300 @18m) — it is NOT gated on
 * a distance change. Returns null for the very first end, for a single-break
 * round, mid-break ends, course rounds (see `isCourseRound` — a course is
 * walked target to target and NEVER shows a stage cue, even when a marked
 * course's per-target segments carry distances), or when there are no segments.
 *
 * Shape (when at a boundary):
 *   {
 *     breakIndex,        // 0-based index of the break just entered (≥1)
 *     prevSegment,       // the break just finished (v2)
 *     segment,           // the break just entered (v2)
 *     distanceChanged,   // true when the native distance differs
 *     faceChanged,       // true when face_type OR face_size_cm differs
 *   }
 *
 * @param {Array|null|undefined} segments  round.segments (v2)
 * @param {number} endNumber  1-indexed end number
 * @param {string|null} [targetFace]  round.target_face — pass it so a marked
 *                                    course is recognised by its face
 * @returns {object|null}
 */
export function breakBoundaryInfo(segments, endNumber, targetFace = null) {
  if (!Array.isArray(segments) || segments.length < 2) return null;
  // Course rounds (Field/Hunter/3D) have no meaningful breaks → no cue.
  if (isCourseRound(targetFace, segments)) return null;

  let cursor = 0;
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    const segEnds = Number(seg?.ends) || 0;
    const start = cursor + 1; // 1-indexed first end of this segment
    cursor += segEnds;
    if (i > 0 && endNumber === start) {
      const prevSegment = segments[i - 1];
      const distanceChanged =
        (prevSegment?.distance ?? null) !== (seg?.distance ?? null) ||
        (prevSegment?.unit ?? "m") !== (seg?.unit ?? "m");
      const faceChanged =
        (prevSegment?.face_type ?? null) !== (seg?.face_type ?? null) ||
        (prevSegment?.face_size_cm ?? null) !== (seg?.face_size_cm ?? null);
      return { breakIndex: i, prevSegment, segment: seg, distanceChanged, faceChanged };
    }
  }
  return null;
}

// ── Catalog-row distance display (#1400, lifted from WizardStepFormat #2066) ──
// The three helpers below format a CATALOG ROW (a round type) rather than a
// round's materialized segments, but they read the same `segment_template`
// shape and answer the same question the rest of this module answers — "what
// distance, in whose unit, does the archer actually see?" — so they belong
// beside formatSegmentDistance rather than inside a wizard component.

/**
 * A catalog row's native unit. The catalog exposes `unit` per round ("m" |
 * "yd"); GNAS imperial and NFAA-outdoor rounds are shot in yards. Defaults to
 * metres for legacy rows that carry no unit.
 *
 * @param {object} rt  a catalog round type
 * @returns {string} "m" | "yd"
 */
export function unitOf(rt) {
  return rt?.unit || "m";
}

/**
 * One distance rendered with its unit, e.g. "100 yd". The value-level sibling
 * of `formatSegmentDistance`, which takes a whole segment object.
 *
 * @param {number|string} value
 * @param {string} unit  "m" | "yd"
 * @returns {string}
 */
export function formatDistance(value, unit) {
  return `${value} ${unit}`;
}

/**
 * Native distance summary for a catalog row, e.g. "100/80/60 yd" or "70 m".
 *
 * Built from `segment_template` — the ONLY source of NATIVE distances plus a
 * per-stage unit. Deliberately NOT `rt.distances`, which carries the LEGACY
 * METRIC numbers for a yard round (a stored York is [91,73,55] m even though it
 * is shot at 100/80/60 yd), so summarising that array mislabels every imperial
 * round (#1400 / M008.8 W4 Defect 1). Falls back to `rt.distances` + the row's
 * own unit only when there is no usable template (legacy/un-backfilled rows).
 *
 * A single-unit template (York/Hereford/Windsor/Metric all are) joins the
 * distances under ONE suffix; a mixed-unit template labels each stage, because
 * a shared suffix would be wrong for at least one of them.
 *
 * @param {object} rt  a catalog round type
 * @returns {string} the summary, or "" when there are no distances
 */
export function distanceSummary(rt) {
  const segs = Array.isArray(rt?.segment_template) ? rt.segment_template : null;
  if (segs && segs.length > 0) {
    const withDist = segs.filter((s) => s.distance != null);
    if (withDist.length > 0) {
      const units = new Set(withDist.map((s) => s.unit || "m"));
      if (units.size === 1) {
        return formatDistance(withDist.map((s) => s.distance).join("/"), [...units][0]);
      }
      return withDist.map((s) => formatDistance(s.distance, s.unit || "m")).join("/");
    }
  }
  const u = unitOf(rt);
  if (rt?.distances?.length === 1) return formatDistance(rt.distances[0], u);
  if (rt?.distances?.length > 1) return formatDistance(rt.distances.join("/"), u);
  return "";
}
